import {
	useJsApiLoader,
	GoogleMap,
	CircleF,
	InfoWindowF,
	MarkerF,
	StandaloneSearchBox
} from "@react-google-maps/api";

// MUI Button (not MDButton): kept identical to the previous InfoWindow content
import Button from "@mui/material/Button";

const { REACT_APP_GOOGLE_MAP_API_KEY } = process.env;

// The Maps script is loaded once for the whole app: the options (libraries
// included) must always be the same, so "places" is always loaded.
// Must be a constant reference (a new array would reload the script).
const LIBRARIES = ["places"];

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };

const MAP_OPTIONS = {
	streetViewControl: false,
	mapTypeControl: false,
	mapTypeId: "roadmap"
};

const CIRCLE_OPTIONS = {
	fillColor: "red",
	fillOpacity: 0.2,
	strokeColor: "red",
	strokeOpacity: 1,
	strokeWeight: 1
};

const INPUT_STYLE = {
	boxSizing: "border-box",
	border: "1px solid transparent",
	width: "240px",
	height: "32px",
	padding: "0 12px",
	borderRadius: "1px",
	boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
	fontSize: "14px",
	outline: "none",
	textOverflow: "ellipsis"
};

/**
 * Google map (@react-google-maps/api) with draggable markers, an optional
 * place search box and a radius circle around the center.
 * Props are the ones provided by GoogleMapHandlerComponent.
 */
export default function GoogleMapComponent({
	zoom,
	center,
	bounds,
	markers,
	withSearchBox,
	onMapMounted,
	onSearchBoxMounted,
	onPlacesChanged,
	onMarkerClick,
	onMarkerClose,
	onMarkerDragEnd,
	radius,
	dispatch,
	addProp
}) {
	const { isLoaded, loadError } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: REACT_APP_GOOGLE_MAP_API_KEY,
		libraries: LIBRARIES
	});

	if (loadError) {
		return <div>Impossible de charger la carte Google Maps.</div>;
	}
	if (!isLoaded) {
		return <div style={{ height: "100%" }} />;
	}

	return (
		<div style={{ position: "relative", height: "100%" }}>
			<GoogleMap
				mapContainerStyle={MAP_CONTAINER_STYLE}
				zoom={zoom}
				center={center}
				onLoad={onMapMounted}
				options={MAP_OPTIONS}
			>
				{markers.map((marker, index) => (
					<MarkerF
						key={index}
						position={marker.position}
						draggable={!!marker.draggable}
						onClick={() => onMarkerClick(marker)}
						onDragEnd={event => onMarkerDragEnd(event, marker)}
						options={marker.icon ? { icon: marker.icon } : {}}
					>
						{/* Info window shown once the marker has been clicked */}
						{marker.showInfo && marker.name && !marker.pinOnly && (
							<InfoWindowF onCloseClick={() => onMarkerClose(marker)}>
								<Button
									size="small"
									sx={{ color: "#49accd", mr: 1, textTransform: "none" }}
									onClick={() => {
										if (addProp) {
											dispatch({
												type: addProp,
												value: marker
											});
										}
									}}
								>
									{marker.name}
								</Button>
							</InfoWindowF>
						)}
					</MarkerF>
				))}

				{center && (
					<CircleF center={center} radius={radius} options={CIRCLE_OPTIONS} />
				)}
			</GoogleMap>

			{withSearchBox && (
				// Overlaid on the top right corner of the map
				<div style={{ position: "absolute", top: 10, right: 60, zIndex: 1 }}>
					<StandaloneSearchBox
						onLoad={onSearchBoxMounted}
						onPlacesChanged={onPlacesChanged}
						bounds={bounds}
					>
						<input
							type="text"
							placeholder="Rechercher un lieu"
							style={INPUT_STYLE}
						/>
					</StandaloneSearchBox>
				</div>
			)}
		</div>
	);
}
